/**
 * Neuron Report Dispatch — shared domain types.
 *
 * These mirror docs/CONTRACT.md §2 (DynamoDB), §3 (windows), §4 (field_map),
 * §5 (rollup) and §6 (column policy) exactly. If the API and this file disagree,
 * the contract wins and this file is the bug.
 *
 * Conventions used throughout:
 *  - snake_case property names, because they are the wire format. Do not camelise.
 *  - Timestamps are ISO-8601 strings UNLESS the name ends in `_at` on a grant/TTL
 *    field, where the contract specifies epoch seconds (`IsoTimestamp` vs `EpochSeconds`
 *    below make this explicit at each site).
 *  - Everything a client "simply lacks" is optional. §4: code must never assume a
 *    column exists.
 */

/** ISO-8601 instant, e.g. `2026-09-16T09:02:11Z`. Render with format.istDateTime(). */
export type IsoTimestamp = string;

/** Seconds since epoch. Grants use these (contract §2 `nrd-grants`). */
export type EpochSeconds = number;

/** Calendar date, `YYYY-MM-DD`, always an IST civil date. */
export type IsoDate = string;

// ---------------------------------------------------------------------------
// Windows (contract §3)
// ---------------------------------------------------------------------------

export type WindowType =
  | 'same_day'
  | 'yesterday'
  | 'last_n_days'
  | 'mtd'
  | 'mtd_yesterday'
  | 'from_date'
  | 'custom';

export type ReportWindow =
  | { type: 'same_day' }
  | { type: 'yesterday' }
  | { type: 'last_n_days'; n: number }
  | { type: 'mtd' }
  | { type: 'mtd_yesterday' }
  | { type: 'from_date'; from: IsoDate }
  | { type: 'custom'; from: IsoDate; to: IsoDate };

/** Ordered for pickers; the label is the console-facing wording. */
export const WINDOW_TYPES: ReadonlyArray<{ value: WindowType; label: string; hint: string }> = [
  { value: 'same_day', label: 'Same day', hint: 'Today, IST' },
  { value: 'yesterday', label: 'Yesterday', hint: 'The previous IST day' },
  { value: 'last_n_days', label: 'Last N days', hint: 'Inclusive, ending yesterday' },
  { value: 'mtd', label: 'Month to date', hint: '1st of this month through today' },
  {
    value: 'mtd_yesterday',
    label: 'Month to date — through yesterday',
    // The window a MORNING schedule wants. Plain 'mtd' ends today, so a 09:30 fire asks
    // for a source covering a day that has barely started and goes stale every morning,
    // while the export for "the 1st to yesterday" sits in the bucket unread.
    hint: '1st of this month through yesterday — use this for a morning schedule',
  },
  { value: 'from_date', label: 'From date', hint: 'A fixed start through today' },
  { value: 'custom', label: 'Custom range', hint: 'Fixed start and end' },
];

/**
 * Contract §3 guardrail. A `detailed` report over a window wider than this is
 * refused by the dispatcher (`status=failed`), so the console must refuse it first
 * rather than letting an operator queue a run that cannot succeed.
 */
export const DETAILED_MAX_WINDOW_DAYS = 3;

// ---------------------------------------------------------------------------
// Clients (contract §2 nrd-clients, §4 field_map, §6 column policy)
// ---------------------------------------------------------------------------

/**
 * Source-column names by role. Every key is optional: a client that has no concept
 * of `rpc` simply omits it. `key` is the borrower/account identity and is NEVER the
 * phone number.
 */
export interface FieldMap {
  key?: string;
  /** e.g. credgenics-ICICI ships `loan_id` with a leading backtick on 100% of rows. */
  key_strip_prefix?: string;
  phone?: string;
  name?: string;
  language?: string;
  timestamp?: string;
  date?: string;
  disposition?: string;
  sub_disposition?: string;
  disposition_category?: string;
  call_status?: string;
  duration?: string;
  attempts?: string;
  recording?: string;
  flow?: string;
  campaign?: string;
  company_id?: string;
  contact_outcome?: string;
  ptp_date?: string;
  ptp_amount?: string;
  committed_amount?: string;
  commitment_type?: string;
  callback_date?: string;
  rpc?: string;
  /** Clients may map roles we have not named yet; keep the shape open. */
  [role: string]: string | undefined;
}

/** Contract §6. `default` ships unless turned off; `opt_in` only when a schedule lists it; `never` is unreachable. */
export interface ColumnPolicy {
  default?: string[];
  opt_in?: string[];
  never?: string[];
}

/** Which source values mean "we actually reached the borrower" (drives the §6 right-party gate). */
export interface ReachedValues {
  rpc_true?: string[];
  contact_outcome?: string[];
  [role: string]: string[] | undefined;
}

/**
 * Per-flow ranking (CONTRACT-FLOWS §1). A client may run several campaigns under one S3
 * prefix, and the meaning of a disposition depends on which — so the rank list belongs to
 * the flow, not the client.
 *
 * Two shapes, mutually exclusive:
 *  - `ranking: 'ranked'` (the default) with its own `disposition_ranks` / `non_contact`.
 *    Either list may be omitted to inherit the client-level one.
 *  - `ranking: 'none'` with a `reason`. There is no best outcome to find here — a one-way
 *    broadcast, or a message-delivery call. Its borrowers read `Not ranked`, which is
 *    neither `Not contacted` nor `Unclassified`.
 */
export type FlowRanking = 'ranked' | 'none';

export interface FlowConfig {
  ranking?: FlowRanking;
  /** Mandatory when `ranking` is `'none'` — it is what the report and this console show
   *  in place of a best outcome. */
  reason?: string;
  disposition_ranks?: string[];
  non_contact?: string[];
}

/** Flow name, exactly as the source spells it, to its configuration. Matching is case-folded. */
export type FlowMap = Record<string, FlowConfig>;

/** The rollup's three non-disposition states (CONTRACT §5, CONTRACT-FLOWS §2/§4). */
export const NOT_CONTACTED = 'Not contacted';
export const UNCLASSIFIED = 'Unclassified';
export const NOT_RANKED = 'Not ranked';

export interface Client {
  client_id: string;
  name: string;
  s3_prefix: string;
  /** The workspace key. Authoritative — NOT derivable from s3_prefix. May be null (fusion). */
  company_id: string | null;
  company_name: string;
  field_map: FieldMap;
  /** Contact outcomes, best first. Matching is case-folded. */
  disposition_ranks: string[];
  /** Outcomes that are explicitly not a contact; never ranked against each other. */
  non_contact?: string[];
  /** Optional per-flow ranking. Absent means one vocabulary for the whole client. */
  flows?: FlowMap;
  reached_values?: ReachedValues;
  /**
   * Campaign names this client's exports have actually contained, most rows first. The
   * schedule editor's flow filter offers these; it used to be a free-text box, and a flow
   * typed from memory that matches nothing builds a report of zeroes rather than failing.
   *
   * `exact` marks a count from a scheduled run, which reads every row. A refresh samples.
   * An empty array with no `flows_refreshed_at` means nobody has looked yet — which is not
   * the same as "this client has no flows".
   */
  /**
   * Where this client's exports arrive from. A lender is one client to the people reading
   * the report and several workspaces inside the platform — Fusion runs Kiran in Debt
   * Collection and Aasha and Roshini in Sales & Marketing, exported separately.
   *
   * A client with one workspace still reports it; `is_multi_workspace` is what the console
   * branches on, so a single-workspace client's screens are unchanged.
   */
  workspaces?: ClientWorkspace[];
  is_multi_workspace?: boolean;
  observed_flows?: ObservedFlow[];
  flows_refreshed_at?: string | null;
  /**
   * The shapes this client may be scheduled for, resolved (never empty from the API).
   * `workbook` is opt-in per client because it is computed against one client's own
   * disposition vocabulary. Absent only on a payload from an older API.
   */
  report_shapes?: ReportShape[];
  column_policy: ColumnPolicy;
  /**
   * CONTRACT-COVERAGE: may a part day be MAILED to this client?
   *
   * Absent or false — the default for all eight clients — means a partial source is
   * refused for delivery exactly as §8.3 has always refused a stale one. True means the
   * client has accepted that they will sometimes get a part day, and the coverage is
   * then stated in the subject, the body and the report itself. It never affects an
   * internal download, which accepts a partial source either way because nothing
   * leaves the building.
   */
  allow_partial_window?: boolean;
  /** Default 7, hard max 7 (contract §1). */
  link_ttl_days: number;
  /** null = keep forever. Expiring a link must never delete data. */
  retention_days?: number | null;
  active: boolean;
  created_at?: IsoTimestamp;
  updated_at?: IsoTimestamp;
}

/** Hard ceiling from contract §1. A per-client override may lower it, never raise it. */
export const MAX_LINK_TTL_DAYS = 7;

export type ClientCreate = Omit<Client, 'created_at' | 'updated_at'>;
export type ClientPatch = Partial<Omit<Client, 'client_id' | 'created_at' | 'updated_at'>>;

// ---------------------------------------------------------------------------
// Recipients (contract §2 nrd-recipients)
// ---------------------------------------------------------------------------

export type RecipientKind = 'to' | 'cc' | 'bcc';
export type BounceState = 'ok' | 'soft' | 'hard' | 'complained';

export const RECIPIENT_KINDS: readonly RecipientKind[] = ['to', 'cc', 'bcc'];

export interface Recipient {
  client_id: string;
  recipient_id: string;
  email: string;
  name: string;
  kind: RecipientKind;
  active: boolean;
  bounce_state: BounceState;
  /** Set when SES suppression kicked in; a suppressed recipient is not deliverable. */
  suppressed_at?: IsoTimestamp | null;
  /**
   * The server's own verdict — active, not suppressed, not hard-bounced or complained.
   * Derived on read, never stored, and the only thing a screen should ask before saying
   * "this desk will receive the report": the four fields it folds have already caught a
   * console that computed deliverability three different ways on three screens.
   */
  sendable?: boolean;
  /** Present when re-enabling could not clear the SES account suppression entry. */
  warning?: string;
  created_at?: IsoTimestamp;
  updated_at?: IsoTimestamp;
}

export type RecipientCreate = Pick<Recipient, 'email' | 'name' | 'kind'> & { active?: boolean };
export type RecipientPatch = Partial<Pick<Recipient, 'email' | 'name' | 'kind' | 'active' | 'bounce_state'>>;

// --- bulk entry (CONTRACT-RECIPIENTS §2) ------------------------------------------

/**
 * POST /api/clients/{id}/recipients/bulk.
 *
 * `input` is a paste or a pre-split list; both go through one parser, so they cannot
 * behave differently. `kind` and `active` apply to every address in the paste — a paste
 * of twelve desks that are all `to` is the case this exists for, and a per-address kind
 * belongs in twelve separate adds.
 */
export interface RecipientBulkCreate {
  input: string | string[];
  kind?: RecipientKind;
  active?: boolean;
}

export type DuplicateReason = 'already_configured' | 'repeated_in_input';

/** Already on the client, or repeated inside this paste. Reported, never an error. */
export interface RecipientBulkDuplicate {
  /** The text as pasted, so the operator can find it in their own clipboard. */
  input: string;
  email: string;
  /** The row it collides with; null when the collision is with an earlier entry. */
  recipient_id: string | null;
  reason: DuplicateReason;
}

export interface RecipientBulkInvalid {
  index: number;
  input: string;
  reason: string;
}

/** 201, or 200 when `created` is empty because every address was already configured. */
export interface RecipientBulkResult {
  created: Recipient[];
  duplicates: RecipientBulkDuplicate[];
  requested: number;
  created_count: number;
  duplicate_count: number;
  /** Every distinct domain in the paste. */
  domains: string[];
}

/**
 * `errors` on a 400 `invalid_email_in_list`. `written: 0` is the promise that matters:
 * half a list written is worse than none, because nobody can tell which half.
 */
export interface RecipientBulkRejection {
  invalid: RecipientBulkInvalid[];
  valid_count: number;
  written: number;
}

/** `errors` on a 500 `bulk_write_failed` — validation passed, an AWS call did not. */
export interface RecipientBulkWriteFailure {
  created: string[];
  failed_at: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Schedules (contract §2 nrd-schedules)
// ---------------------------------------------------------------------------

export interface ClientWorkspace {
  key: string;
  name: string;
  slug: string;
  s3_prefix: string;
  company_id?: string | null;
  active?: boolean;
}

export interface ObservedFlow {
  name: string;
  rows: number;
  /** True when a scheduled run counted it; false when a sample only saw it. */
  exact?: boolean;
  last_seen?: string;
  source?: string;
  /** Which of the client's workspaces this campaign runs in. */
  workspace?: string;
}

export interface ObservedFlowsResult {
  client_id: string;
  flows: ObservedFlow[];
  flows_refreshed_at: string;
  objects_read: string[];
  sampled: boolean;
  note: string;
}

export type ReportShape = 'detailed' | 'borrower' | 'workbook';

export interface ReportShapeInfo {
  value: ReportShape;
  /** Title case, to open a sentence. */
  label: string;
  /** The badge mark. */
  glyph: string;
  /** Lower case, to close one: `Detailed (one row per call)`. */
  gloss: string;
  /** One line: the picker's hint and the badge's tooltip, which want the same sentence. */
  hint: string;
  /** What `rows_out` counts, as a word: "12 read → 4 connected". */
  rowNoun: string;
  /** Follows the column count on the dry run, comma and all. */
  columnsNote: string;
}

const BORROWER_SHAPE: ReportShapeInfo = {
  value: 'borrower',
  label: 'Borrower',
  glyph: '≡',
  gloss: 'one row per account',
  hint: 'One row per account, rolled up (§5)',
  rowNoun: 'emitted',
  columnsNote: ', in this order',
};

/**
 * Every shape, once. Six screens used to spell this out as `x === 'detailed' ? a : b`,
 * which is not a rendering choice but an assumption that there are only two — so a third
 * would have arrived labelled "Borrower" on all six, with no error anywhere.
 */
export const REPORT_SHAPES: ReadonlyArray<ReportShapeInfo> = [
  BORROWER_SHAPE,
  {
    value: 'detailed',
    label: 'Detailed',
    glyph: '≣',
    gloss: 'one row per call',
    hint: 'One row per call — capped at 3 days (§3)',
    rowNoun: 'emitted',
    columnsNote: ', in this order',
  },
  {
    value: 'workbook',
    label: 'Workbook',
    glyph: '▦',
    gloss: 'six analysis sheets over two data sheets',
    hint: 'Six analysis sheets computed live over Borrower-wise and Call Dump. Always xlsx.',
    // The workbook holds connected calls only, so rows_out sits an order of magnitude
    // below rows_in and reads like a mistake unless the screen says what it counted.
    rowNoun: 'connected',
    columnsNote: ' across Borrower-wise and Call Dump',
  },
];

/**
 * Describe a shape read off a run or a schedule. A stored value is whatever the dispatcher
 * wrote, which on a tab left open across a deploy is a shape this bundle has never heard
 * of — show it verbatim rather than quietly calling it a borrower report.
 */
export function reportShapeInfo(value: string | null | undefined): ReportShapeInfo {
  if (value === null || value === undefined || value === '') return BORROWER_SHAPE;
  return (
    REPORT_SHAPES.find((entry) => entry.value === value) ?? {
      value: value as ReportShape,
      label: value,
      glyph: '?',
      gloss: 'a shape this console does not know',
      hint: 'Produced by a newer dispatcher than this console — reload to pick it up.',
      rowNoun: 'emitted',
      columnsNote: ', in this order',
    }
  );
}

export type ReportFormat = 'csv' | 'xlsx';

export interface Schedule {
  schedule_id: string;
  client_id: string;
  report_shape: ReportShape;
  window: ReportWindow;
  /** Report on ONE workspace. Null reads every workspace the client exports from. */
  workspace?: string | null;
  /** Nullable filter on the source `flow` column. */
  flow: string | null;
  /** Nullable glob/regex on campaign name. */
  campaign_pattern: string | null;
  /** Cron expression, evaluated in IST. */
  cron: string;
  /** Opt-in columns this client explicitly selected (contract §6). */
  include_opt_in: string[];
  /**
   * Which of the client's recipients this schedule mails (CONTRACT-RECIPIENTS §1).
   *
   * Absent and `[]` mean the SAME thing — every sendable recipient of the client — and
   * that is today's behaviour, so no stored schedule changes meaning. Reading empty as
   * "send to nobody" would silently switch off every schedule in the table on deploy
   * day; there is no value here that means nobody, and that state is `enabled: false`.
   *
   * It holds ids, never addresses: `bounce_state`, `active` and `suppressed_at` live on
   * the recipient row, so an id stays closed when that desk hard-bounces. A literal
   * address would be resolved into an unconditionally-sendable recipient and walk
   * straight past suppression.
   */
  recipient_ids?: string[];
  format?: ReportFormat;
  enabled: boolean;
  last_run_at?: IsoTimestamp | null;
  next_run_at?: IsoTimestamp | null;
  name?: string;
  created_at?: IsoTimestamp;
  updated_at?: IsoTimestamp;
}

export type ScheduleCreate = Omit<
  Schedule,
  'schedule_id' | 'last_run_at' | 'next_run_at' | 'created_at' | 'updated_at'
>;
export type SchedulePatch = Partial<Omit<Schedule, 'schedule_id' | 'client_id' | 'created_at' | 'updated_at'>>;

// ---------------------------------------------------------------------------
// Source coverage (CONTRACT-COVERAGE, amending §8.3)
// ---------------------------------------------------------------------------

/**
 * How much of the requested window the source file actually contained.
 *
 * WHY THIS EXISTS. The Neuron export is produced by a human pressing "Export Report", so it
 * can be run before the day's calling has finished. The file's last-modified time does NOT
 * tell you what is inside it: fusion's 16 Sep export was written at 15:57 IST and the latest
 * call in it is 13:29 IST, while the client's own dashboard shows calls on that date until
 * 17:53 IST. An earlier draft defined coverage as `min(window_end, mtime)`, which would have
 * labelled that very file "covers to 15:57" — a smaller lie, still a lie. `covered_to` is
 * derived from the DATA: the latest dialed timestamp in the rows the reader walked.
 *
 * These are the field names `services/api/runs.py::public_run` and
 * `services/api/downloads.py::_download_item` actually put on the wire. Do not rename them
 * on the way in; the console renders the backend's vocabulary, including its word for the
 * thing — a PART DAY, as the subject line and the workbook cell both call it.
 */
export interface SourceCoverage {
  /**
   * `full` | `partial` | `unmeasured`, from `nrd.coverage`. The backend's own verdict, and
   * the authority: `unmeasured` is a THIRD state — a source with no time-of-day column, or
   * any path that did not read the data — and a console that renders it as "complete"
   * tells the lie this feature prevents. Empty string on a run written before the field
   * existed.
   */
  coverage_state?: string | null;
  /** False for `unmeasured`. Redundant with `coverage_state`; sent because it is cheap. */
  coverage_measured?: boolean | null;
  /**
   * True when the data stops before the window it claims to cover. A client delivery is
   * refused unless the client sets `allow_partial_window`; an internal download
   * (`deliver` false) is accepted and labelled.
   */
  window_partial?: boolean | null;
  /**
   * The latest dialed timestamp present in the source, capped at the window end, RENDERED
   * — `2026-09-16 13:29 IST`, through `nrd.windows.fmt_ist`. Not an ISO instant: the ISO
   * `covered_to` is on the stored run row but is not in the public allowlist. Empty string,
   * not null, when there is nothing to report — and an empty value is "not measured", which
   * is NOT the same as "covers the whole window".
   */
  covered_to_ist?: string | null;
  /**
   * Rows whose width did not match the header, so the reader could not put their values
   * under the right column names. Measured at 70,006 of 185,115 rows (37.8%) in fusion's
   * 13 Sep export — two row shapes under one header. Coverage is never computed from these
   * rows, and their count is a reason to distrust every figure in the file.
   */
  rows_misaligned?: number | null;
}

/**
 * The dry run's `coverage` block, which is a DIFFERENT and weaker thing from a run's.
 *
 * A dry run reads a bounded PREFIX of the source, and these exports are ordered by attempt
 * round — so the latest dialed time inside a prefix is not the latest in the file. The
 * backend therefore reports `unmeasured` here always, on purpose, and carries the reason in
 * `note`. The console must not dress that up as a measurement: an estimate on this screen
 * would be believed precisely because everything else on it is real.
 */
export interface DryRunCoverage {
  coverage_state?: string | null;
  coverage_measured?: boolean | null;
  window_partial?: boolean | null;
  covered_to_ist?: string | null;
  rows_misaligned?: number | null;
  window_end_ist?: string;
  latest_dialed_ist?: string;
  /** Upper bound on coverage, never the coverage itself. */
  source_last_modified_ist?: string;
  /** Why coverage is not measured here rather than merely missing. */
  note?: string;
  sentence?: string;
}

// ---------------------------------------------------------------------------
// Runs (contract §2 nrd-runs)
// ---------------------------------------------------------------------------

/**
 * `stale` is not a failure of ours — it means the source object was missing or older
 * than the window, so nothing was sent (contract §8.3). Render it as its own state,
 * never merged into `failed`.
 */
export type RunStatus = 'resolving' | 'stale' | 'generating' | 'sending' | 'sent' | 'failed';

export const RUN_STATUSES: readonly RunStatus[] = [
  'resolving',
  'stale',
  'generating',
  'sending',
  'sent',
  'failed',
];

/** Statuses that are still moving; screens poll while a run is in one of these. */
export const RUN_IN_FLIGHT: readonly RunStatus[] = ['resolving', 'generating', 'sending'];

export type ArtifactKind = 'report' | 'preview' | 'manifest' | 'recording_index';

export interface RunArtifact {
  kind: ArtifactKind | string;
  /** Always in nrd-artifacts-442228338612. Never in neuron-report. */
  s3_key: string;
  bytes: number;
  rows: number;
}

export interface RunRecipient {
  recipient_id: string;
  email: string;
  kind: RecipientKind;
  /** Present once the grant for this recipient has been minted. */
  token_hash?: string;
  status?: 'pending' | 'sent' | 'bounced' | 'complained' | 'suppressed';
}

export interface Run extends SourceCoverage {
  run_id: string;
  client_id: string;
  schedule_id: string | null;
  started_at: IsoTimestamp;
  finished_at?: IsoTimestamp | null;
  status: RunStatus;
  /** Source objects read from s3://neuron-report/ (read-only). */
  source_keys: string[];
  rows_in: number;
  rows_out: number;
  format: ReportFormat;
  report_shape?: ReportShape;
  window?: ReportWindow;
  /**
   * The IST civil dates the relative window resolved to at fire time. The API has always
   * sent these (`public_run` allow-lists them); the type omitted them, so Dashboard
   * re-declared them locally and the Runs list could only ever show the spec word
   * "Yesterday" with no dates beside it. Coverage needs the dates: "covered to 13:29"
   * means nothing without the day it was asked to cover.
   */
  window_from?: IsoDate;
  window_to?: IsoDate;
  window_label?: string;
  /** False for an internal download (POST /api/downloads) — generated, never mailed. */
  deliver?: boolean;
  artifacts: RunArtifact[];
  recipients: RunRecipient[];
  ses_message_ids: string[];
  error?: string | null;
  /**
   * Contract §5: a disposition absent from `client.disposition_ranks` is NOT silently
   * sorted last — it lands here so the console can surface it for a ranking decision.
   */
  unranked_dispositions: string[];
  /** Exact column list emitted, for the dry-run/audit view (contract §8.4). */
  columns?: string[];
  triggered_by?: string;
  /**
   * A test mail (CONTRACT-RECIPIENTS §3/§4). Orthogonal to `status`, never a status
   * value: the run vocabulary is closed and validated on write. Excluded from every list
   * and every per-client figure by default — a test must not inflate a client's delivery
   * numbers — so a row carrying this only appears when `include_test` was asked for.
   */
  is_test?: boolean;
  /**
   * Ids named on the schedule that no longer resolve to a recipient row. The run still
   * went to the ids that did resolve — the client needs their report — but a schedule
   * quietly mailing fewer people than it lists is the failure this field exists to make
   * impossible.
   */
  recipients_unknown?: string[];
}

/**
 * POST /api/schedules/{sid}/test-mail — CONTRACT-RECIPIENTS §3.
 *
 * `to` defaults to the signed-in operator. It must be on an allow-listed internal
 * domain, and that is enforced server-side in `services/api/validate.py` and again in
 * the dispatcher: the mail carries real borrower data for a named lender, and a browser
 * check is bypassed by a `curl`. Nothing in this console is the control.
 */
export interface TestMailRequest {
  to?: string;
}

export interface TestMailResult {
  run_id: string;
  status: RunStatus;
  is_test: boolean;
  to: string;
  link_ttl_days: number;
  schedule_id: string;
  client_id: string;
  window_label?: string;
}

/** `errors` on a 403 `test_mail_external_domain` or `test_mail_disabled`. */
export interface TestMailRejection {
  allowed_domains?: string[];
}

/**
 * The link life of a test send, fixed at one day rather than the client's seven
 * (CONTRACT-RECIPIENTS §4). A constant, not a knob. The server is the authority and
 * echoes the number it actually used back in `link_ttl_days`; this exists so the dialog
 * can state the consequence BEFORE the operator commits to sending real borrower data.
 */
export const TEST_MAIL_LINK_TTL_DAYS = 1;

/** POST /api/schedules/{sid}/dry-run — contract §8.4: source key, recipients, columns, counts. */
export interface DryRunResult {
  schedule_id: string;
  client_id: string;
  report_shape: ReportShape;
  window: ReportWindow;
  resolved_from: IsoDate;
  resolved_to: IsoDate;
  /** Resolved source object keys and whether each one is actually there. */
  source_keys: string[];
  missing_source_keys: string[];
  /** True when a source object is missing or older than the window (§8.3). */
  stale: boolean;
  stale_reason?: string | null;
  /**
   * CONTRACT-COVERAGE §7. Always `unmeasured` — see `DryRunCoverage`. Freshness and
   * coverage are different questions and this screen can only answer the first.
   */
  coverage?: DryRunCoverage;
  /**
   * Misaligned rows IN THE SAMPLE, not in the file. A separate field from the run's
   * `rows_misaligned` because it is a separate quantity, and labelling a sample figure as
   * a file figure is how 1,284 becomes mistaken for 70,006.
   */
  rows_misaligned_in_sample?: number;
  /** The backend's own verdict on whether pressing Send now would actually send. */
  would_send?: boolean;
  recipients: RunRecipient[];
  /** The exact column list that would be emitted, in order. */
  columns: string[];
  /** Opt-in columns requested but blocked, with the reason (policy `never`, not selected, gated). */
  suppressed_columns: Array<{ column: string; reason: string }>;
  rows_in: number;
  rows_out: number;
  estimated_bytes?: number;
  unranked_dispositions: string[];
  /** CONTRACT-FLOWS: the client's flows, what the sample met, and how many of its rows sit
   *  in a flow that declares no best outcome. Empty / zero for a client with no flows. */
  flows?: FlowRankingView[];
  flow_counts?: Record<string, number>;
  rows_not_ranked_sample?: number;
  unmatched_flows?: string[];
  not_ranked_label?: string;
  /** Plain-English warnings, in the order an operator should read them. */
  notes?: string[];
  /** Populated when the §3 detailed>3-day guardrail would refuse this run. */
  guardrail_error?: string | null;
}

// ---------------------------------------------------------------------------
// Grants and access (contract §1, §2 nrd-grants / nrd-access)
// ---------------------------------------------------------------------------

export type GrantKind = 'report' | 'recording';

export interface Grant {
  /** sha256 of the token. The token itself is never stored and never returned. */
  token_hash: string;
  run_id: string;
  client_id: string;
  recipient_id: string;
  s3_key: string;
  kind: GrantKind;
  issued_at: EpochSeconds;
  /** OUR window, not the S3 signature's. Exact, survives credential rotation. */
  expires_at: EpochSeconds;
  revoked_at?: EpochSeconds | null;
  /** = expires_at + 86400. One day of grace so expired hits render 410, not 404. */
  ttl?: EpochSeconds;
  /** Denormalised for the console list. */
  recipient_email?: string;
  access_count?: number;
  /** The API returns nrd-access.accessed_at, which is an ISO-8601 IST string, not an epoch.
   *  Typed as both because older rows and direct DynamoDB reads can carry either;
   *  format.toDate() accepts both. */
  last_accessed_at?: IsoTimestamp | EpochSeconds | null;
  last_access_outcome?: AccessOutcome | null;
  access_count_truncated?: boolean;
}

/** Derived link state — see format.linkState(). Encoded in form as well as colour. */
export type LinkState = 'live' | 'expiring' | 'expired' | 'revoked';

export type AccessOutcome = 'ok' | 'expired' | 'revoked';

export interface AccessRow {
  token_hash: string;
  /** ISO-8601 with the IST offset — the sort key of nrd-access. */
  accessed_at: IsoTimestamp | EpochSeconds;
  ip: string;
  user_agent: string;
  outcome: AccessOutcome;
}

// ---------------------------------------------------------------------------
// Downloads (contract §7 /api/downloads)
// ---------------------------------------------------------------------------

export interface DownloadQuery {
  client_id?: string;
  from?: IsoDate;
  to?: IsoDate;
  shape?: ReportShape;
  audio_only?: boolean;
  cursor?: string;
  limit?: number;
}

export interface DownloadItem extends SourceCoverage {
  run_id: string;
  client_id: string;
  client_name?: string;
  shape: ReportShape;
  from: IsoDate;
  to: IsoDate;
  generated_at: IsoTimestamp;
  artifacts: RunArtifact[];
  rows: number;
  bytes: number;
  /** Token hash of the console operator's own grant, if one has been minted. */
  token_hash?: string | null;
  expires_at?: EpochSeconds | null;
  revoked_at?: EpochSeconds | null;
  audio_only?: boolean;
}

/** POST /api/downloads — generate on demand; returns a run to follow. */
export interface DownloadCreate {
  client_id: string;
  shape: ReportShape;
  window: ReportWindow;
  include_opt_in?: string[];
  flow?: string | null;
  campaign_pattern?: string | null;
  format?: ReportFormat;
  audio_only?: boolean;
}

export interface DownloadCreateResult {
  run_id: string;
  status: RunStatus;
}

// ---------------------------------------------------------------------------
// Dispositions (contract §7 /api/dispositions/{client_id}, §5 ranking)
// ---------------------------------------------------------------------------

/** One flow as the API returns it: what is stored, plus what the fold will actually use. */
export interface FlowRankingView {
  flow: string;
  ranking: FlowRanking;
  ranked: boolean;
  reason: string;
  disposition_ranks: string[];
  non_contact: string[];
  /** True when this flow has no list of its own and falls back to the client's. */
  inherits_ranks: boolean;
  inherits_non_contact: boolean;
  /** What the fold resolves for this flow: its own list, else the client's. */
  effective_ranks: string[];
  effective_non_contact: string[];
  /** `Not ranked` for a flow that declares no best outcome; empty otherwise. */
  borrower_label: string;
}

export interface DispositionConfig {
  client_id: string;
  /** Contact outcomes, best first. Case-folded on match. */
  disposition_ranks: string[];
  /** Explicitly-not-a-contact values; these are never ranked against each other. */
  non_contact: string[];
  /** Per-flow ranking (CONTRACT-FLOWS §1). Empty for a client that runs one vocabulary. */
  flows?: FlowRankingView[];
  has_flows?: boolean;
  /** The source column flows are matched on, from the client's field_map. */
  flow_column?: string;
  /** The rollup's own words for its three non-disposition states, so nothing hard-codes them. */
  labels?: { not_contacted: string; unclassified: string; not_ranked: string };
  /**
   * Values seen in real exports that are in neither list. Surfaced so a human makes
   * the ranking call — never auto-sorted to the bottom.
   */
  unranked: Array<{ value: string; seen_count?: number; first_seen_at?: IsoTimestamp; last_seen_at?: IsoTimestamp }>;
  updated_at?: IsoTimestamp;
  updated_by?: string;
}

/**
 * PATCH body. `flows` is the whole map, not a delta: a flow left out is deleted, which is
 * how the screen removes one.
 */
export type DispositionPatch = Partial<Pick<DispositionConfig, 'disposition_ranks' | 'non_contact'>> & {
  flows?: FlowMap;
};

// ---------------------------------------------------------------------------
// SES events (contract §2 nrd-ses-events)
// ---------------------------------------------------------------------------

export type SesEventType =
  | 'Send'
  | 'Delivery'
  | 'Bounce'
  | 'Complaint'
  | 'Reject'
  | 'Open'
  | 'Click'
  | 'DeliveryDelay'
  | 'RenderingFailure';

export interface SesEvent {
  message_id: string;
  /** The instant the event occurred, IST with offset. Parseable — see `event_key`. */
  event_at: IsoTimestamp;
  event_at_ist?: string;
  /**
   * The row's sort key, `{occurred_at}#{event_type}#{recipient}`. One bounce can name
   * several recipients at the same instant, so this — not `event_at` — is what makes a
   * list key unique. It is a key and never a timestamp; nothing parses it.
   */
  event_key?: string;
  /** SES's own spelling: `Delivery`, `DeliveryDelay`, `RenderingFailure`. */
  event_type: SesEventType | string;
  /** Our normalised vocabulary for the same event: `delivery`, `delivery_delay`. */
  event_code?: string;
  recipient: string;
  run_id: string;
  /** The stored detail map rendered as one line. The map itself is `detail_fields`. */
  detail?: string | null;
  detail_fields?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Sent mail archive (CONTRACT-MAIL §2 nrd-messages)
// ---------------------------------------------------------------------------

/**
 * Status of one recipient's copy. It only ever moves FORWARD
 * (`sent` → `delivered` → `opened`), and `bounced` / `complained` / `rejected` are
 * terminal — a late delivery never overwrites a bounce (CONTRACT-MAIL §3). The console
 * must therefore render a terminal status as terminal, not as "probably fine by now".
 */
export type MessageStatus =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'bounced'
  | 'complained'
  | 'rejected';

export const MESSAGE_STATUSES: readonly MessageStatus[] = [
  'sent',
  'delivered',
  'opened',
  'bounced',
  'complained',
  'rejected',
];

/** Terminal, and bad. These never progress and must be unmistakable in a long list. */
export const MESSAGE_FAILED_STATUSES: readonly MessageStatus[] = [
  'bounced',
  'complained',
  'rejected',
];

/** Which body part is being looked at. Both were sent, so both are archived (§1). */
export type MessagePart = 'html' | 'text';

/** What the mail offered, as recorded at send time. Filename, not the s3 key. */
export interface MessageArtifact {
  kind: ArtifactKind | string;
  filename: string;
  bytes: number;
  rows: number;
}

/** One recipient's copy, minus the body — what GET /api/messages returns. */
export interface Message {
  /** The SES message id. Partition key of nrd-messages. */
  message_id: string;
  run_id: string;
  client_id: string;
  client_name?: string;
  recipient_id: string;
  recipient_email: string;
  recipient_kind: RecipientKind;
  subject: string;
  /** ISO-8601 carrying the IST offset — stored for display, not for arithmetic. */
  sent_at: IsoTimestamp;
  /** GSI sort key on `by_sent`. */
  sent_at_epoch?: EpochSeconds;
  status: MessageStatus;
  /** Most recent SES event for this message, verbatim. */
  last_event?: string | null;
  last_event_at?: IsoTimestamp | null;
  open_count: number;
  artifacts: MessageArtifact[];
  /** Archive keys in nrd-artifacts-442228338612 (§1). Both parts, because both were sent. */
  s3_html?: string;
  s3_text?: string;
  /** False means the send predates the archive — not that the copy was lost. */
  archived?: boolean;
  /**
   * When the links in this copy stop working, from OUR 7-day window (CONTRACT §1) — an
   * UPPER bound, because a client may override the TTL downwards, and it says nothing
   * about revocation. The authority per link is `GET /api/runs/{run_id}/grants`; this
   * exists so a list of a few hundred messages does not cost a query each.
   */
  links_expire_at?: EpochSeconds | null;
  links_live?: boolean;
  /** Pre-formatted IST strings the API also sends. Screens format from the instant. */
  sent_at_ist?: string;
  last_event_at_ist?: string;
  links_expire_at_ist?: string;
  /**
   * This copy was a test send (CONTRACT-RECIPIENTS §4). Written as data at archive time,
   * never inferred from the subject string. Excluded from this list by default, and from
   * `open_count` and every per-client delivery figure with it — an operator checking what
   * a client will see must not move that client's numbers.
   */
  is_test?: boolean;
  /** 400 days, matching nrd-access: the archive outlives the links. */
  ttl?: EpochSeconds;
}

/**
 * The archived body. Stored exactly as sent, tokenised links and all — it is an archive,
 * not a live document, and those links obey the original 7-day window. Either part can
 * be null on its own; the read is capped, and `truncated` says the part shown is only
 * its head.
 */
export interface MessageBody {
  html: string | null;
  text: string | null;
  truncated?: boolean;
}

/** Why there is no body. A machine reason; `body_missing_detail` is the sentence. */
export type BodyMissingReason = 'not_archived' | 'object_missing' | 'unreadable' | (string & {});

/**
 * GET /api/messages/{message_id} — the row plus the archived body.
 *
 * `body: null` with a reason is a 200, not an error: the message was sent and its
 * archived copy is gone, which is a gap an operator needs to see rather than a failure
 * of the request (§4).
 */
export type MessageDetail = Message & {
  body: MessageBody | null;
  body_missing?: BodyMissingReason | null;
  body_missing_detail?: string;
};

export interface MessageQuery {
  client_id?: string;
  status?: MessageStatus;
  /** IST calendar dates. Calendar only — the console offers no relative presets. */
  from?: IsoDate;
  to?: IsoDate;
  cursor?: string;
  limit?: number;
  /** Opt test sends back in. Default is exclude, on the server (CONTRACT-RECIPIENTS §5). */
  include_test?: boolean;
}

/**
 * POST /api/messages/{message_id}/download — mints a short-lived OPERATOR grant.
 * It never reuses the recipient's token: an operator opening a file is a different
 * audit event from the client opening their link (CONTRACT-MAIL §4).
 */
export interface MessageDownloadRequest {
  kind: string;
}

export interface MessageDownloadResult {
  url: string;
  kind?: string;
  filename?: string;
  expires_at?: EpochSeconds;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/**
 * Normalised cursor page. The API may answer with `cursor` or `next_cursor`;
 * lib/api.ts always hands back `cursor`. `cursor === null` means "last page".
 */
export interface Page<T> {
  items: T[];
  cursor: string | null;
  /** How many exist in total, not how many this page holds. */
  total?: number;
  /** True when the server stopped enumerating before the end. Never present the page
   *  length as the count when this is set. */
  truncated?: boolean;
}

export interface ListParams {
  cursor?: string;
  limit?: number;
}

/** Signed-in operator, decoded from the Cognito ID token. */
export interface Identity {
  sub: string;
  email: string;
  name?: string;
  username?: string;
  groups: string[];
}
